// Environment configuration abstraction – provides safe access in runtime & tests

interface AppEnv {
  DEV: boolean;
  PROD: boolean;
  MODE: string;
  API_URL: string;
  APP_VERSION: string;
  APP_BUILD_DATE: string;
  APP_COMMIT_HASH: string;
}

// Narrow the raw env shape to supported primitive types for easier parsing

type RawEnv = Record<string, string | boolean | undefined>;

/**
 * Extended globalThis for test environments where import.meta is mocked.
 */
interface GlobalThisWithMockMeta {
  importMeta?: {
    env?: RawEnv;
  };
}

const toBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return undefined;
};

const toString = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  return undefined;
};

/** Resolves the raw environment record from globalThis mock or import.meta. */
function resolveRawEnv(): RawEnv {
  try {
    const globalWithMock = globalThis as GlobalThisWithMockMeta;
    if ('importMeta' in globalThis && globalWithMock.importMeta?.env) {
      return (globalWithMock.importMeta.env ?? {}) as RawEnv;
    }
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env as RawEnv;
    }
  } catch {
    // ignore
  }
  return {};
}

/** Builds the AppEnv from a raw environment record, applying defaults. */
function buildAppEnv(rawEnv: RawEnv): AppEnv {
  const nodeEnv = toString(rawEnv.NODE_ENV) ?? 'development';

  return {
    DEV: toBoolean(rawEnv.DEV) ?? nodeEnv === 'development',
    PROD: toBoolean(rawEnv.PROD) ?? nodeEnv === 'production',
    MODE: toString(rawEnv.MODE) ?? nodeEnv,
    API_URL: toString(rawEnv.VITE_API_URL) ?? 'http://localhost:8000',
    APP_VERSION: toString(rawEnv.VITE_APP_VERSION) ?? 'unknown',
    APP_BUILD_DATE: toString(rawEnv.VITE_APP_BUILD_DATE) ?? 'unknown',
    APP_COMMIT_HASH: toString(rawEnv.VITE_APP_COMMIT_HASH) ?? 'unknown',
  } as const;
}

/**
 * Builds and returns the resolved application environment configuration.
 *
 * Delegates to resolveRawEnv for raw environment variable access and
 * buildAppEnv for normalization and defaults.
 *
 * @returns The fully resolved AppEnv configuration object.
 */
function getEnv(): AppEnv {
  return buildAppEnv(resolveRawEnv());
}

export const env = getEnv();
export const { DEV, PROD, MODE, API_URL, APP_VERSION, APP_BUILD_DATE, APP_COMMIT_HASH } = env;
/** Returns true if the application is running in development mode. */
export const isDevelopment = (): boolean => DEV;
/** Returns true if the application is running in production mode. */
export const isProduction = (): boolean => PROD;
/** Returns true if the application is running in test mode. */
export const isTest = (): boolean => MODE === 'test';
